import React, { useEffect, useState, useCallback } from "react";
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Space,
    Typography,
    Card,
    message,
    Popconfirm,
    Row,
    Col,
    Tag,
    Select,
} from "antd";
import {
    PlusOutlined,
    ReloadOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import {
    getLawOfficerNamesApi,
    createLawOfficerNameApi,
    updateLawOfficerNameApi,
    deleteLawOfficerNameApi,
    getLawOfficersApi,
} from "../../../../api/master.api";

const { Title } = Typography;
const { Option } = Select;

const RelatedLawOfficerName = () => {
    const [items, setItems] = useState([]);
    const [lawOfficers, setLawOfficers] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();

    // 🔥 Fetch Law Officer Names
    const fetchData = useCallback(async (page = 1, limit = 10, search = "") => {
        setLoading(true);
        try {
            const { data } = await getLawOfficerNamesApi(page, limit, search);
            setItems(data.data.items || []);
            setPagination({
                current: data.data.pagination.page,
                pageSize: data.data.pagination.limit,
                total: data.data.pagination.total,
            });
        } catch (error) {
            message.error("Failed to load law officer names");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    // 🔥 Fetch Law Officers for dropdown
    const fetchLawOfficers = async () => {
        try {
            const { data } = await getLawOfficersApi(1, 100);
            setLawOfficers(data.data.items || []);
        } catch (error) {
            message.error("Failed to load law officers");
        }
    };

    useEffect(() => {
        fetchData(1, pagination.pageSize, searchText);
        fetchLawOfficers();
    }, [fetchData, pagination.pageSize, searchText]);

    // 🔥 Create
    const handleCreate = async (values) => {
        try {
            await createLawOfficerNameApi(values);
            message.success("Law officer name created successfully");
            setCreateOpen(false);
            createForm.resetFields();
            fetchData(1, pagination.pageSize, searchText);
        } catch (error) {
            message.error("Create failed");
            console.error(error);
        }
    };

    // 🔥 Update
    const handleUpdate = async (values) => {
        try {
            await updateLawOfficerNameApi(selected.officer_name_id, values);
            message.success("Law officer name updated successfully");
            setEditOpen(false);
            editForm.resetFields();
            fetchData(pagination.current, pagination.pageSize, searchText);
        } catch (error) {
            message.error("Update failed");
            console.error(error);
        }
    };

    // 🔥 Delete
    const handleDelete = async (id) => {
        try {
            await deleteLawOfficerNameApi(id);
            message.success("Law officer name deleted successfully");
            fetchData(pagination.current, pagination.pageSize, searchText);
        } catch (error) {
            message.error("Delete failed");
            console.error(error);
        }
    };

    // 🔥 Table Columns
    const columns = [
        {
            title: "ID",
            dataIndex: "officer_name_id",
            width: 80,
            key: "officer_name_id",
        },
        {
            title: "Related Law Officer",
            dataIndex: "law_officer_name",
            key: "law_officer_name",
            width: 200,
        },
        {
            title: "Officer Name",
            dataIndex: "officer_name",
            key: "officer_name",
            width: 200,
        },
        {
            title: "Address",
            dataIndex: "officer_address",
            key: "officer_address",
            ellipsis: true,
            width: 250,
        },
        {
            title: "Phone Number",
            dataIndex: "officer_mobile",
            key: "officer_mobile",
            width: 150,
        },
        {
            title: "Action",
            key: "action",
            width: 120,
            fixed: "right",
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        onClick={() => {
                            setSelected(record);
                            editForm.setFieldsValue({
                                law_officer_id: record.law_officer_id,
                                officer_name: record.officer_name,
                                officer_address: record.officer_address,
                                officer_mobile: record.officer_mobile,
                            });
                            setEditOpen(true);
                        }}
                    />
                    <Popconfirm
                        title="Delete this officer name?"
                        description="Are you sure you want to delete this?"
                        onConfirm={() => handleDelete(record.officer_name_id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: "20px" }}>
            <Row justify="space-between" style={{ marginBottom: 20 }}>
                <Col>
                    <Title level={4}>সংশ্লিষ্ট আইন কর্মকর্তার নাম (Related Law Officer Name)</Title>
                    <Tag color="blue" style={{ marginTop: 5 }}>
                        Total: {pagination.total}
                    </Tag>
                </Col>
                <Col>
                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => fetchData(pagination.current, pagination.pageSize, searchText)}
                        >
                            Refresh
                        </Button>
                        <Input.Search
                            placeholder="Search officer name..."
                            allowClear
                            onSearch={(value) => {
                                setSearchText(value);
                                fetchData(1, pagination.pageSize, value);
                            }}
                            onChange={(e) => {
                                if (!e.target.value) {
                                    setSearchText("");
                                    fetchData(1, pagination.pageSize, "");
                                }
                            }}
                            enterButton={<SearchOutlined />}
                            style={{ width: 250 }}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setCreateOpen(true)}
                        >
                            Add New Officer Name
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Card>
                <Table
                    columns={columns}
                    dataSource={items}
                    rowKey="officer_name_id"
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                            `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                        onChange: (page, pageSize) => {
                            fetchData(page, pageSize, searchText);
                        },
                        onShowSizeChange: (current, size) => {
                            fetchData(1, size, searchText);
                        },
                    }}
                    scroll={{ x: 1024 }}
                />
            </Card>

            {/* CREATE MODAL */}
            <Modal
                title="Add Law Officer Name"
                open={createOpen}
                onCancel={() => {
                    setCreateOpen(false);
                    createForm.resetFields();
                }}
                footer={null}
                destroyOnClose
                width={600}
            >
                <Form
                    form={createForm}
                    onFinish={handleCreate}
                    layout="vertical"
                    initialValues={{
                        law_officer_id: undefined,
                        officer_name: "",
                        officer_address: "",
                        officer_mobile: "",
                    }}
                >
                    <Form.Item
                        name="law_officer_id"
                        label="Law Officer"
                        rules={[{ required: true, message: "Please select law officer" }]}
                    >
                        <Select
                            placeholder="Select law officer"
                            size="large"
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {lawOfficers.map((officer) => (
                                <Option key={officer.law_officer_id} value={officer.law_officer_id}>
                                    {officer.law_officer_name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="officer_name"
                        label="Officer Name"
                        rules={[
                            { required: true, message: "Please enter officer name" },
                            { min: 2, message: "Minimum 2 characters required" },
                        ]}
                    >
                        <Input
                            placeholder="Enter officer name"
                            size="large"
                            autoFocus
                        />
                    </Form.Item>

                    <Form.Item
                        name="officer_address"
                        label="Address"
                        rules={[{ required: true, message: "Please enter address" }]}
                    >
                        <Input.TextArea
                            placeholder="Enter address"
                            rows={3}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="officer_mobile"
                        label="Phone Number"
                        rules={[
                            { required: true, message: "Please enter phone number" },
                            { pattern: /^[0-9]{11}$/, message: "Please enter valid 11 digit phone number" },
                        ]}
                    >
                        <Input
                            placeholder="Enter phone number"
                            size="large"
                            maxLength={11}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                            <Button onClick={() => setCreateOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Create
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* EDIT MODAL */}
            <Modal
                title="Edit Related Law Officer Name"
                open={editOpen}
                onCancel={() => {
                    setEditOpen(false);
                    editForm.resetFields();
                }}
                footer={null}
                destroyOnClose
                width={600}
            >
                <Form
                    form={editForm}
                    onFinish={handleUpdate}
                    layout="vertical"
                >
                    <Form.Item
                        name="law_officer_id"
                        label="Law Officer"
                        rules={[{ required: true, message: "Please select law officer" }]}
                    >
                        <Select
                            placeholder="Select law officer"
                            size="large"
                            showSearch
                            optionFilterProp="children"
                        >
                            {lawOfficers.map((officer) => (
                                <Option key={officer.law_officer_id} value={officer.law_officer_id}>
                                    {officer.law_officer_name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="officer_name"
                        label="Officer Name"
                        rules={[
                            { required: true, message: "Please enter officer name" },
                            { min: 2, message: "Minimum 2 characters required" },
                        ]}
                    >
                        <Input
                            placeholder="Enter officer name"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="officer_address"
                        label="Address"
                        rules={[{ required: true, message: "Please enter address" }]}
                    >
                        <Input.TextArea
                            placeholder="Enter address"
                            rows={3}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="officer_mobile"
                        label="Phone Number"
                        rules={[
                            { required: true, message: "Please enter phone number" },
                            { pattern: /^[0-9]{11}$/, message: "Please enter valid 11 digit phone number" },
                        ]}
                    >
                        <Input
                            placeholder="Enter phone number"
                            size="large"
                            maxLength={11}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                            <Button onClick={() => setEditOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Update
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default RelatedLawOfficerName;